import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  FileText, 
  Search, 
  BookOpen, 
  Database, 
  Edit3,
  ChevronDown,
  ChevronUp,
  Copy,
  Download
} from "lucide-react";
import NavBar from "@/components/NavBar";

export default function BusinessCaseDeveloper() {
  const [selectedCase, setSelectedCase] = useState("strategic");
  const [searchTerm, setSearchTerm] = useState("");
  const [draftContent, setDraftContent] = useState("");
  const [showGuidance, setShowGuidance] = useState(true);
  const [showRepository, setShowRepository] = useState(true);

  const caseElements = [
    { id: "strategic", title: "Strategic Case", description: "Context and case for change" },
    { id: "economic", title: "Economic Case", description: "Value for money assessment" },
    { id: "commercial", title: "Commercial Case", description: "Procurement and contracting" },
    { id: "financial", title: "Financial Case", description: "Funding and affordability" },
    { id: "management", title: "Management Case", description: "Deliverability and governance" }
  ];

  const guidanceContent = {
    strategic: {
      title: "Strategic Case Guidance",
      sections: [
        "1. Define the problem and opportunity",
        "2. Establish strategic context",
        "3. Make the case for change",
        "4. Set out spending objectives",
        "5. Define success criteria"
      ],
      keyRequirements: [
        "Clear problem definition",
        "Strategic alignment",
        "Stakeholder analysis",
        "Baseline position",
        "Success metrics"
      ]
    },
    economic: {
      title: "Economic Case Guidance",
      sections: [
        "1. Options identification",
        "2. Benefits identification",
        "3. Cost estimation",
        "4. Risk assessment",
        "5. Value for money analysis"
      ],
      keyRequirements: [
        "Options appraisal",
        "Benefits quantification",
        "Cost-benefit analysis",
        "Sensitivity analysis",
        "Value for money statement"
      ]
    }
    // Add other cases as needed
  };

  const repositoryExamples = [
    {
      title: "Healthcare Infrastructure Project",
      type: "Strategic Case",
      excerpt: "The case for change is driven by increasing demand for healthcare services...",
      tags: ["Healthcare", "Infrastructure", "Public Health"]
    },
    {
      title: "Digital Transformation Initiative",
      type: "Economic Case", 
      excerpt: "Benefits include improved efficiency, reduced costs, and enhanced service delivery...",
      tags: ["Digital", "Technology", "Efficiency"]
    },
    {
      title: "Transport Network Upgrade",
      type: "Commercial Case",
      excerpt: "Procurement strategy based on competitive dialogue procedure...",
      tags: ["Transport", "Infrastructure", "Procurement"]
    }
  ];

  const currentGuidance = guidanceContent[selectedCase as keyof typeof guidanceContent];

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-2">Business Case Developer</h1>
            <p className="text-muted-foreground">
              Comprehensive business case development with guidance, templates, and repository access
            </p>
          </div>

          {/* Case Element Selection */}
          <Tabs value={selectedCase} onValueChange={setSelectedCase} className="mb-6">
            <TabsList className="grid w-full grid-cols-5">
              {caseElements.map((element) => (
                <TabsTrigger key={element.id} value={element.id} className="text-xs">
                  {element.title}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Three-Panel Layout */}
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Left Panel - Guidance */}
            <div className={`lg:col-span-3 space-y-4 ${!showGuidance ? 'lg:col-span-1' : ''}`}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen className="h-4 w-4" />
                      Guidance
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowGuidance(!showGuidance)}
                    >
                      {showGuidance ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardHeader>
                {showGuidance && (
                  <CardContent className="pt-0">
                    <ScrollArea className="h-96">
                      {currentGuidance && (
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold mb-2">Key Sections</h4>
                            <ul className="space-y-1 text-sm">
                              {currentGuidance.sections.map((section, index) => (
                                <li key={index} className="text-muted-foreground">{section}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2">Requirements</h4>
                            <div className="flex flex-wrap gap-1">
                              {currentGuidance.keyRequirements.map((req, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {req}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                )}
              </Card>
            </div>

            {/* Center Panel - Draft Area */}
            <div className={`lg:col-span-6 ${!showGuidance && !showRepository ? 'lg:col-span-10' : !showGuidance || !showRepository ? 'lg:col-span-8' : ''}`}>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Edit3 className="h-5 w-5" />
                    Draft Your {caseElements.find(e => e.id === selectedCase)?.title}
                  </CardTitle>
                  <CardDescription>
                    {caseElements.find(e => e.id === selectedCase)?.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Start drafting your business case content here. Use the guidance on the left and examples on the right to inform your writing..."
                    value={draftContent}
                    onChange={(e) => setDraftContent(e.target.value)}
                    className="min-h-96 mb-4"
                  />
                  <div className="flex gap-2">
                    <Button size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                    <Button size="sm" variant="outline">
                      Save Draft
                    </Button>
                    <Button size="sm" variant="outline">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Repository & Search */}
            <div className={`lg:col-span-3 space-y-4 ${!showRepository ? 'lg:col-span-1' : ''}`}>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Database className="h-4 w-4" />
                      Repository
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowRepository(!showRepository)}
                    >
                      {showRepository ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardHeader>
                {showRepository && (
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search examples..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      
                      <ScrollArea className="h-80">
                        <div className="space-y-3">
                          {repositoryExamples
                            .filter(example => 
                              example.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              example.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              example.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
                            )
                            .map((example, index) => (
                            <Card key={index} className="p-3 hover:bg-muted/50 cursor-pointer">
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <h5 className="font-medium text-sm">{example.title}</h5>
                                  <Badge variant="secondary" className="text-xs">
                                    {example.type}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {example.excerpt}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {example.tags.map((tag, tagIndex) => (
                                    <Badge key={tagIndex} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </ScrollArea>
                      
                      <Button size="sm" variant="outline" className="w-full">
                        Import Template
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}